import CloseIcon from '@mui/icons-material/Close';
import { Box, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { Widget } from '@typeform/embed-react';
import config from 'config';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { spaces } from '../../../assets/styles/themes/tokens';
import { campaign, onboardingHints, searchFilterKeys } from '../../../constants';
import { selectSearchFilters } from '../../../reducers/search.selectors';
import { ampliTrackMessageDisplayedEvent, MessageType } from '../../../services/track.service';
import type { SearchFilters } from '../../../types/search';
import { getCurrentSearchId } from '../../../utils/ampli';
import { safeSessionStorage } from '../../../utils/safeStorage';
import {
	getSearchFeedbackDialogRemainingDelay,
	getSearchFeedbackDialogShownAt,
	isSearchFeedbackDialogRouteAllowed,
	searchFeedbackDialogEvents,
	searchFeedbackDialogStorageKeys,
} from '../../../utils/searchFeedbackDialog';
import useScreenSize from '../../../utils/useScreenSize';
import useSearchExperiments from '../../../utils/useSearchExperiments';
import { useTriggeredHint } from '../../../utils/useTriggeredHint';

const getAppliedFilters = (filters: SearchFilters): string[] => {
	const applied: string[] = [];

	for (const key of searchFilterKeys) {
		if (key === 'classYear' && filters.minClassYear != null && filters.maxClassYear != null) {
			applied.push(`classYear: ${filters.minClassYear}-${filters.maxClassYear}`);
			continue;
		}
		if (key === 'inclusionAdapted' && filters.inclusionAdapted) {
			applied.push('inclusionAdapted: true');
			continue;
		}
		const value = filters[key as keyof SearchFilters];
		if (Array.isArray(value) && value.length > 0) {
			applied.push(`${key}: ${value.join(', ')}`);
		}
	}

	return applied;
};

interface SearchFeedbackDialogProps {
	pathname: string;
	userId?: string;
}

const SearchFeedbackDialog: React.FC<SearchFeedbackDialogProps> = ({ pathname, userId = '' }) => {
	const { experimentName, experimentVariant } = useSearchExperiments();
	const widgetId = config.searchFeedbackDialog?.widgetId || '';
	const { t } = useTranslation();
	const { isXsScreen, isSmScreen } = useScreenSize();
	const [isOpen, setIsOpen] = useState(false);
	const searchFilters = useSelector(selectSearchFilters);
	const searchQuery = searchFilters?.term || '';
	const filterApplied = useMemo(
		() => JSON.stringify(searchFilters ? getAppliedFilters(searchFilters) : []),
		[searchFilters]
	);
	const [searchId, setSearchId] = useState('');
	const isMobile = isXsScreen || isSmScreen;
	const timeoutRef = useRef<number | null>(null);
	const shouldAllowRoute = isSearchFeedbackDialogRouteAllowed(pathname);
	const { hasSeen, record } = useTriggeredHint(onboardingHints.searchFeedback.shown);

	const clearScheduledOpen = useCallback(() => {
		if (timeoutRef.current !== null) {
			window.clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, []);

	const markDialogAsClosed = useCallback(() => {
		safeSessionStorage.removeItem(searchFeedbackDialogStorageKeys.submittedAt);
		setSearchId('');
		setIsOpen(false);
	}, []);

	const scheduleDialogOpen = useCallback(() => {
		clearScheduledOpen();

		if (!shouldAllowRoute) {
			return;
		}

		if (hasSeen) {
			return;
		}

		const submittedAtRaw = safeSessionStorage.getItem(searchFeedbackDialogStorageKeys.submittedAt);
		if (!submittedAtRaw) {
			return;
		}

		const submittedAt = Number(submittedAtRaw);
		if (Number.isNaN(submittedAt)) {
			return;
		}

		const timeoutMs = getSearchFeedbackDialogRemainingDelay(submittedAt);

		timeoutRef.current = window.setTimeout(() => {
			safeSessionStorage.removeItem(searchFeedbackDialogStorageKeys.submittedAt);
			setSearchId(getCurrentSearchId() || '');
			setIsOpen(true);
			timeoutRef.current = null;
		}, timeoutMs);
	}, [clearScheduledOpen, shouldAllowRoute, hasSeen]);

	// Migrates the legacy per-browser localStorage seen flag to the server-backed
	// hint. Self-healing: retries every session until the account state has it.
	// Gated on userId so the effect re-fires once the user has loaded — recording
	// before that would silently no-op in the thunk.
	useEffect(() => {
		if (!userId || hasSeen) {
			return;
		}
		if (getSearchFeedbackDialogShownAt()) {
			record();
		}
	}, [userId, hasSeen, record]);

	useEffect(() => {
		if (!shouldAllowRoute) {
			setIsOpen(false);
			return clearScheduledOpen;
		}

		scheduleDialogOpen();
		return clearScheduledOpen;
	}, [clearScheduledOpen, scheduleDialogOpen, shouldAllowRoute]);

	useEffect(() => {
		const handleSubmitted = () => {
			if (isOpen) {
				setSearchId(getCurrentSearchId() || '');
			} else {
				scheduleDialogOpen();
			}
		};
		window.addEventListener(searchFeedbackDialogEvents.submitted, handleSubmitted);

		return () => {
			window.removeEventListener(searchFeedbackDialogEvents.submitted, handleSubmitted);
		};
	}, [isOpen, scheduleDialogOpen]);

	// The display itself is the permanent seen state — recorded server-side so
	// the survey never reappears on another device, reload, or navigation.
	useEffect(() => {
		if (!isOpen) {
			return;
		}

		record();
	}, [isOpen, record]);

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		ampliTrackMessageDisplayedEvent(campaign.searchRelevanceFeedback, MessageType.DIALOG);
	}, [isOpen]);

	return (
		<Dialog
			open={isOpen}
			onClose={markDialogAsClosed}
			hideBackdrop={!isMobile}
			disableEnforceFocus={!isMobile}
			disableScrollLock={!isMobile}
			PaperProps={{
				sx: {
					width: isMobile ? '343px' : '440px',
					maxWidth: 'calc(100vw - 16px)',
					margin: 0,
					...(isMobile
						? {}
						: {
								position: 'fixed',
								right: spaces.lg,
								bottom: spaces.xxxl,
							}),
				},
			}}
			sx={
				isMobile
					? undefined
					: {
							pointerEvents: 'none!important',
							'& .MuiDialogContent-root, & .MuiDialogTitle-root': {
								pointerEvents: 'all!important',
							},
						}
			}
		>
			<DialogTitle
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					paddingY: spaces.md,
					paddingX: spaces.lg,
				}}
			>
				{t('Your impression of the search')}
				<IconButton aria-label="close" onClick={markDialogAsClosed} sx={{ padding: spaces.xxs }}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent
				sx={{
					padding: 0,
					overflow: 'hidden',
					height: isMobile ? '70vh' : 'min(560px, calc(100vh - 160px))',
				}}
			>
				{/* Typeform Widget must fill the container; we apply dimensions via the parent's sx and
            '& > *' because the linter forbids passing a style prop to custom components. */}
				<Box
					sx={{
						width: '100%',
						height: '100%',
						'& > *': { width: '100%', height: '100%' },
					}}
				>
					<Widget
						hidden={{
							user_id: userId,
							search_query: searchQuery,
							// Typeform hidden fields accept only string values.
							is_new_search: 'true',
							search_id: searchId,
							filter_applied: filterApplied,
							experiment_name: experimentName,
							experiment_variant: experimentVariant,
						}}
						id={widgetId}
						onSubmit={markDialogAsClosed}
					/>
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export default SearchFeedbackDialog;
